import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";

import { Grid, Radio, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import classnames from "classnames";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { AiOutlinePlusCircle } from "react-icons/ai";

import { useAppState } from "../../../../context/StateContext";
import { AgentServiceBooking } from "../../../../models/agent.service.booking";
import { Currency } from "../../../../models/currency";
import { EstimateGroup, EstimatePrice } from "../../../../models/estimate";
import { PriceGroup } from "../../../../models/price.group";
import { RoomJob } from "../../../../models/sales";
import CurrencyService from "../../../../services/currency.service";
import PriceGroupService from "../../../../services/price.group.service";
import { Status } from "../../../../util/enums";
import { getMeasurement } from "../../../../util/getMeasurement";
import { getTotalItemsValuesByInventory } from "../../../../util/inventory";
import {
  formatCurrency,
  getTotalCostsByPrice,
  mountNewGroup,
  TotalPriceCost,
} from "../../../../util/prices";
import { Button } from "../../../Form";
import { Color } from "../../../Form/Button";
import SkeletonComponent from "../../Skeleton";
import { NotificationToast } from "../../Toast/NotificationToast";
import { GroupSection } from "../GroupSection";
import { AllowedColumn } from "../types";
import { NewGroupSection } from "./NewGroupSection";
import useStyles from "./styles";

interface EstimatePriceSectionProps {
  jobEstimateId?: number;
  agentId?: number;
  prices: EstimatePrice[];
  onChangePrices?: (prices: EstimatePrice[]) => void;
  isEstimateMode?: boolean;
  allowedColumns: AllowedColumn[];
  ["data-cy"]?: string;
  inventory?: RoomJob[];
  isLoading?: boolean;
  agentServices?: AgentServiceBooking[];
  isOnViewMode?: boolean;
  onCustomerView?: boolean;
  selectedPriceId?: number;
  setSelectedPriceId?: (value: number) => void;
  isEstimateApproved?: boolean;
  currency?: Currency;
}

const EstimatePriceSection: React.FC<EstimatePriceSectionProps> = (props) => {
  const {
    prices,
    onChangePrices,
    jobEstimateId,
    agentId,
    allowedColumns,
    isEstimateMode = true,
    inventory,
    isLoading,
    agentServices = [],
    isOnViewMode = false,
    onCustomerView = false,
    selectedPriceId,
    setSelectedPriceId,
    isEstimateApproved = false,
    currency: currencyOnCustomerView,
  } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const { currentUser } = useAppState();
  const currency = onCustomerView ? currencyOnCustomerView : currentUser?.company?.currency;
  const measurement = getMeasurement();

  const [allCurrencies, setAllCurrencies] = useState<Currency[]>([]);

  const [, setNewGroupName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { isFetching: loadingCurrencies } = useQuery(
    ["currencies.list"],
    () =>
      CurrencyService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setAllCurrencies(response.data);
      },
    }
  );

  const { isFetching: loadingGroups, data: allGroups } = useQuery(
    ["price.groups.list"],
    async () => {
      const response = await PriceGroupService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
        include: JSON.stringify([{ association: "services", where: { status: Status.ACTIVE } }]),
      });

      if (response?.data instanceof Array) {
        return response.data;
      }

      return [];
    },
    {
      enabled: !!currentUser?.companyId,
    }
  );

  const getDataCy = useCallback(
    (key: string) => `${props["data-cy"]}${props["data-cy"] ? "-" : ""}${key}`,
    [props["data-cy"]]
  );

  const totalInventoryValues = useMemo(
    () => getTotalItemsValuesByInventory(inventory, measurement),
    []
  );

  const defaultGroupName = useMemo(() => `${translate("newGroup")}`, [translate]);

  const onAddNewPrice = useCallback(() => {
    if (onChangePrices) {
      const length = prices?.length;

      onChangePrices([
        ...(prices || []),
        {
          companyId: currentUser?.companyId,
          groups: [],
          sequence: (length || 0) + 1,
          jobEstimateId,
          agentId,
        },
      ]);
      NotificationToast.success(translate("priceCreatedSuccessfully"), { id: "new-price-success" });
    }
  }, [prices]);

  const onDeleteprice = useCallback(
    (priceIndex: number) => {
      if (onChangePrices) {
        const clonePrices = _.cloneDeep((prices || []) as EstimatePrice[]);

        if (clonePrices[priceIndex]) {
          clonePrices.splice(priceIndex, 1);
          onChangePrices(clonePrices);
        }
      }
    },
    [prices]
  );

  const onAddNewGroup = useCallback(
    (priceIndex: number, existentGroups?: PriceGroup[]) => {
      if (!onChangePrices) return;

      const clonePrices = _.cloneDeep((prices || []) as EstimatePrice[]);

      let groupsToAdd = [
        mountNewGroup({
          companyId: currentUser?.companyId,
          currency,
          defaultGroupName,
        }),
      ];

      if (existentGroups && existentGroups.length) {
        groupsToAdd = existentGroups.map((existentGroup) =>
          mountNewGroup({
            companyId: currentUser?.companyId,
            currency,
            defaultGroupName,
            group: existentGroup,
          })
        );
      }

      clonePrices[priceIndex] = {
        ...clonePrices[priceIndex],
        groups: [...(clonePrices[priceIndex].groups || []), ...groupsToAdd],
      };

      onChangePrices(clonePrices);
      setNewGroupName(defaultGroupName);

      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      NotificationToast.success(translate("groupAddedSuccessfully"), {
        id: "new-group-success",
      });
    },
    [prices, inputRef, allGroups]
  );

  const onChangeGroups = (priceIndex: number, groups: EstimateGroup[]) => {
    if (onChangePrices) {
      const clonePrices = _.cloneDeep((prices || []) as EstimatePrice[]);

      if (clonePrices[priceIndex] && clonePrices[priceIndex].groups) {
        clonePrices[priceIndex].groups = groups;
        onChangePrices(clonePrices);
      }
    }
  };

  const renderSymbolTotalByPrice = (totalCost: TotalPriceCost) => {
    const { estimate } = totalCost;
    const keys = Object.keys(estimate);
    const values = Object.values(estimate);

    return (
      <Grid item container direction="row" className={classes.widthAuto}>
        <Grid
          item
          container
          direction="column"
          className={classnames(classes.widthAuto, classes.gap4, classes.mr16)}
        >
          {keys.map((key) => (
            <Grid item className={classes.totalPriceSymbolValue} key={key}>
              <span className={classes.totalPriceSymbol}>{key}</span>
            </Grid>
          ))}
        </Grid>
        <Grid
          item
          container
          direction="column"
          alignItems="flex-end"
          className={classnames(classes.widthAuto, classes.gap4)}
        >
          {values.map((value, index) => {
            const currency = allCurrencies.find((currency) => currency.code === keys[index]);

            return (
              <Grid item key={index}>
                <span className={classes.totalPriceValue}>{formatCurrency(value, currency)}</span>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  };

  const renderTotalCostsByPrice = (price?: EstimatePrice) => {
    const totalCost = getTotalCostsByPrice(price, inventory, currency, allCurrencies, measurement);

    const { estimate } = totalCost;

    if (Object.keys(estimate).length > 0) {
      return (
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          className={classes.totalPriceContainer}
        >
          <Grid item>
            <span className={classes.totalPriceTitle}>
              {getTotalPriceTitleBySequence(price?.sequence)}
            </span>
          </Grid>
          {renderSymbolTotalByPrice(totalCost)}
        </Grid>
      );
    }

    return null;
  };

  const getTotalPriceTitleBySequence = useCallback((sequence?: number) => {
    return `${translate("totalOf")} ‘${translate("price")} ${sequence ?? ""}’`;
  }, []);

  useEffect(() => {
    if (!allCurrencies.length && onCustomerView) {
      const groups = prices.flatMap((price) => price.groups);
      const services = groups.flatMap((group) => group?.estimateServices);
      const currencies = services.flatMap((service) => service?.currency);

      const uniqueCurrency = _.uniqBy(currencies, "id");

      setAllCurrencies(uniqueCurrency as Currency[]);
    }
  }, [onCustomerView]);

  return (
    <Grid container direction="column" data-cy={getDataCy("prices")}>
      {!!isEstimateMode && (
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          className={classes.priceHeader}
        >
          <Grid item>
            <span className={classes.subTitle} data-cy={getDataCy("prices-title")}>
              {translate("prices")}
            </span>
          </Grid>
          {!isOnViewMode && !isEstimateApproved && (
            <Grid item>
              <Button
                customColor={Color.PRIMARY}
                onClick={() => onAddNewPrice()}
                data-cy={getDataCy("prices-button-new_price")}
              >
                <AiOutlinePlusCircle
                  color="white"
                  size={20}
                  className={classes.mr12}
                  data-cy={getDataCy("prices-button-new_price-icon")}
                />
                <span
                  className={classes.buttonLabel}
                  data-cy={getDataCy("prices-button-new_price-label")}
                >
                  {translate("createNewPrice")}
                </span>
              </Button>
            </Grid>
          )}
          {isOnViewMode && onCustomerView ? (
            <div>
              {selectedPriceId && (
                <h3>
                  <b>{translate("selectedPrice")}</b> {translate("price")}{" "}
                  {prices.find((price) => Number(price.id) === selectedPriceId)?.sequence}
                </h3>
              )}
            </div>
          ) : null}
        </Grid>
      )}

      <SkeletonComponent isLoading={isLoading || loadingCurrencies} classesName={classes.skeleton}>
        <>
          {onCustomerView && !prices.length && (
            <span>{translate("youHaventAddedAnyPriceYet")}</span>
          )}
          {prices?.map((price, priceIndex) => {
            return (
              <Grid
                key={price.sequence}
                data-cy={getDataCy(`prices-${price.sequence}`)}
                container
                direction="column"
                className={classnames(
                  classes.priceContainer,
                  !isEstimateMode && classes.priceContainerNotShowed,
                  onCustomerView && classes.priceContainerOnCustomerView
                )}
              >
                {!!isEstimateMode && (
                  <Grid
                    data-cy={getDataCy(`prices-${price.sequence}-content`)}
                    container
                    direction="row"
                    justifyContent="space-between"
                    className={classes.groupHeader}
                  >
                    <Grid
                      item
                      container
                      alignItems="center"
                      direction="row"
                      data-cy={getDataCy(`prices-${price.sequence}-sequence`)}
                      xs={12}
                      md={6}
                    >
                      {onCustomerView && (
                        <Radio
                          className={classes.radioButton}
                          checked={selectedPriceId === Number(price.id)}
                          onClick={() => setSelectedPriceId && setSelectedPriceId(Number(price.id))}
                        />
                      )}
                      <span className={classes.subTitle}>
                        {translate("price")} {price.sequence}
                      </span>
                    </Grid>
                    <Grid
                      item
                      container
                      direction="row"
                      alignItems="center"
                      className={classes.widthAuto}
                    >
                      {priceIndex !== 0 && !onCustomerView && !isEstimateApproved && !isOnViewMode && (
                        <Button ghost={true} onClick={() => onDeleteprice(priceIndex)}>
                          <Tooltip title={translate("deleteThisPrice")} placement="top">
                            <span className={classes.priceDelete}>{translate("delete")}</span>
                          </Tooltip>
                        </Button>
                      )}
                      {!isOnViewMode && !isEstimateApproved && (
                        <SkeletonComponent
                          isLoading={isLoading || loadingGroups}
                          classesName={classes.skeleton}
                        >
                          <NewGroupSection
                            groups={allGroups || []}
                            onAddBlankGroup={() => onAddNewGroup(priceIndex)}
                            onAddExistingGroup={(groups: PriceGroup[]) =>
                              onAddNewGroup(priceIndex, groups)
                            }
                          />
                        </SkeletonComponent>
                      )}
                    </Grid>
                  </Grid>
                )}

                <GroupSection
                  isOnViewMode={isOnViewMode || isEstimateApproved}
                  allCurrencies={allCurrencies}
                  priceIndex={priceIndex}
                  groups={price?.groups}
                  isEstimateMode={isEstimateMode}
                  onChangeGroups={onChangeGroups}
                  priceSequence={price.sequence}
                  allowedColumns={allowedColumns}
                  totalInventoryValues={totalInventoryValues}
                  agentServices={agentServices}
                  onCustomerExpandedGroups={
                    onCustomerView ? price?.groups?.map((_, index) => index) : []
                  }
                  onCustomerView={onCustomerView}
                />

                {renderTotalCostsByPrice(price)}
              </Grid>
            );
          })}
        </>
      </SkeletonComponent>
    </Grid>
  );
};

export default React.memo(EstimatePriceSection);
